/**
 * Whether the dev tools exist at all (the brand-mark long-press menu and everything it opens).
 *
 * TWO ways in, because the tools serve two audiences:
 *
 *  - `devToolsAvailable` — a **dev build**. `process.env.NODE_ENV` is inlined by the bundler, so
 *    this is a compile-time constant: on localhost everything is on with no round-trip, and the
 *    tools that can ONLY work there (the demo-account switcher, which the dev proxy performs) stay
 *    behind this one and nothing else.
 *  - `devToolsUnlocked()` — a dev build **or** a tab the server has granted access to on a deployed
 *    origin. Designers and PMs QA the live app, so the long-press has to work on symphony.wix.com —
 *    for them and no one else. The grant is a server verdict on the caller's IP being inside the Wix
 *    corporate network (`lib/dev-tools-network.ts` → `/api/dev-tools-access`, asked by
 *    `lib/dev-tools-access.ts`); this module only remembers the answer.
 *
 * The answer is cached in `sessionStorage` so it survives the reload the mock-data panel triggers,
 * and so the data layer can read it SYNCHRONOUSLY on the first request of a page load — otherwise a
 * designer's faked counts wouldn't apply until an async re-check landed, i.e. never in time.
 *
 * That cache is a convenience, not the door: the server is what grants access in the first place,
 * and a tab holding a grant re-checks it on every load (`revalidateDevToolsAccess`). Someone who
 * forges the key by hand gets developer chrome aimed at their own browser — a menu that replays
 * animations, and list lengths faked in their own view. No data, no identity, no privilege. Nothing
 * that matters may ever be gated this way.
 *
 * Kept import-free on purpose: the mock layer reads the sync gate, and pulling the mount resolver
 * (`api-base`) in through it would evaluate window-dependent module state everywhere that layer
 * loads. The fetching half is `lib/dev-tools-access.ts`.
 */

export const devToolsAvailable = process.env.NODE_ENV !== 'production';

/**
 * How long a dev-tools gesture has to be HELD. One constant because the app has two of them — the
 * brand mark opening the dev menu (`BrandHeader`) and the chat's expand/shrink control opening the
 * conversation monitor (`FloatingChat/use-conversation-monitor.ts`) — and they are the same gesture as
 * far as a hand is concerned: a dev who has learned the hold on one must not find the other ignoring
 * it. Both also swallow the release, so a hold this long never doubles as the control's normal click.
 */
export const DEV_LONG_PRESS_MS = 500;

const SESSION_KEY = 'orion-web:dev-tools-unlocked';

function sessionStore(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.sessionStorage;
  } catch {
    return undefined; // storage blocked (private mode) — treat as no grant
  }
}

/** Whether the dev tools may be used right now. Safe during SSR (always `false` there). */
export function devToolsUnlocked(): boolean {
  if (devToolsAvailable) return true;
  try {
    return sessionStore()?.getItem(SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

/** Record the server's verdict for this tab — or clear it the moment the server says no. */
export function rememberDevToolsAccess(allowed: boolean): void {
  try {
    if (allowed) sessionStore()?.setItem(SESSION_KEY, '1');
    else sessionStore()?.removeItem(SESSION_KEY);
  } catch {
    /* storage unavailable — the in-memory verdict still carries this page load */
  }
}
